/**
 * Temporary shared types for Bucket 2 (form widgets) TS error sweep.
 * Follow-up: merge augmentations into FormikInput / EditionForm when bucket 5 lands.
 */
import type { ComponentProps, FC, FormHTMLAttributes, ReactNode } from 'react';
import type { AnySchema } from 'yup';
import { Form as FormikFormType } from 'formik';
import type { FormikContextType, FormikHelpers, FormikProps } from 'formik';
import type { FormikInputProps as BaseFormikInputProps } from 'Components/frameworks/formik/FormikInput';
import { FormikInput as BaseFormikInput } from 'Components/frameworks/formik/FormikInput';
import { EditionForm as BaseEditionForm } from 'Components/widgets/edition-form/EditionForm';
import { Toast as BaseToast } from 'Components/frameworks/toast/Toast';

export type ContactInfoAttendeeValues = Record<string, unknown> & {
  checkAll?: boolean;
  optinsCGU?: Record<string, { channel: string; optin: boolean; optin_partners: boolean }>;
};

/** Contact-info Formik values shape used across attendee forms. */
export type ContactInfoFormValues = {
  adults: ContactInfoAttendeeValues[];
  children: ContactInfoAttendeeValues[];
  seller?: Record<string, unknown>;
};

/** Partial ProfileCompanion for optional initialValues defaults. */
export type PartialProfileCompanion = Partial<ProfileCompanion>;

/** Partial OptinsInfo for optional optinsInfo defaults. */
export type PartialOptinsInfo = Partial<OptinsInfo>;

export type FormikSubmitHandler<T extends Record<string, unknown> = Record<string, unknown>> = (
  values: T,
  bag?: FormikHelpers<T>,
) => void | Promise<unknown>;

/** FormikInput accepts yup schemas at runtime; widen props for bucket-local call sites. */
export type BucketFormikInputProps = BaseFormikInputProps & {
  autoComplete?: string;
  additionnalClasses?: string;
  className?: string;
  countryFieldName?: string;
  dateFormat?: string;
  errorMessage?: string;
  dayPlaceholder?: string;
  iconColor?: string;
  iconName?: string;
  isDateRange?: boolean;
  max?: number;
  maxDate?: Date;
  maxLength?: number;
  min?: number;
  minDate?: Date;
  monthPlaceholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLElement> | Event) => void;
  onClick?: (e: React.MouseEvent | KeyboardEvent) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  options?:
    | Array<{ value: string; name: string; label?: string; [key: string]: unknown }>
    | ReadonlyArray<{ value: string; name: string; label?: string; [key: string]: unknown }>;
  disabledDays?: ReadonlyArray<{ from: string; to: string }>;
  phoneCodeFieldName?: string;
  phoneCodeLabel?: string;
  phoneCodeOptions?: Array<{
    value: string;
    name: string;
    shortName?: string;
    countryCode?: string;
  }>;
  phoneCodePlaceholder?: string;
  placeholder?: string;
  yearPlaceholder?: string;
  optimized?: boolean;
  validate?: (() => boolean) | AnySchema | ((...args: unknown[]) => boolean);
};

export const BucketFormikInput = BaseFormikInput as FC<BucketFormikInputProps>;

type BucketFormikFormProps = FormHTMLAttributes<HTMLFormElement> & {
  id?: string;
  'data-testid'?: string;
};

/** Formik Form wrapper: avoids React 18/19 HTML attribute variance on form element. */
export const BucketFormikForm = FormikFormType as FC<BucketFormikFormProps>;

type BucketToastProps = {
  children?: ReactNode;
  className?: string;
  color?: Colors;
  icon?: string;
  isFixed?: boolean;
  label?: string;
  onClose: () => unknown;
  portalName?: string;
  visible?: boolean;
};

/** Toast wrapper: corrects forwardRef generic mismatch in source component. */
export const BucketToast = BaseToast as FC<BucketToastProps>;

export type EditionFormRenderProps = {
  focusNextInput: (e: KeyboardEvent) => void;
  formik: FormikProps<Record<string, unknown>>;
  hasUnicode: boolean;
  locale: string;
};

type BucketEditionFormProps = Omit<ComponentProps<typeof BaseEditionForm>, 'children'> & {
  children: (props: EditionFormRenderProps) => ReactNode;
};

/** EditionForm wrapper: children may return ReactNode instead of DOM Node. */
export const BucketEditionForm = BaseEditionForm as FC<BucketEditionFormProps>;

/** Minimal AppContext stub for contact-info specs. */
export const minimalContactInfoAppContext = { locale: 'fr-FR' } as AppContext;

/** No-op Formik onSubmit for specs that only render children. */
export const noopFormikSubmit = (): void => undefined;

/** Cast partial Formik context mocks in widget specs. */
export const asFormikContext = <T>(ctx: unknown): FormikContextType<T> =>
  ctx as FormikContextType<T>;

/** Widen readonly form field options for BucketFormikInput call sites. */
export const asFormFieldOptions = (
  options: ReadonlyArray<{ value: string; name: string; label?: string; [key: string]: unknown }>,
): Array<{ value: string; name: string; label?: string; [key: string]: unknown }> =>
  options as Array<{ value: string; name: string; label?: string; [key: string]: unknown }>;

/** Cast prebooking/proposal query params at call sites. */
export const asProposalsQueryParam = (params: unknown) => params as never;
